import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isProgressChangedError } from './roundProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import {
  completeDigitRecallRound,
  completeCargoSortingRound,
  completeMemoryRound,
  completeOddOneOutRound,
  completePatternRecognitionRound,
  completeReflexRound,
  completeRound,
  fetchParentMiniTaskBootstrap,
  launchParentMiniTask,
  timeoutWordBuildRound,
} from './miniTasksApi';
import { clearMiniTaskCountdown } from './useMiniTaskCountdown';
import {
  playMiniTaskSound, prepareMiniTaskSound, readMiniTaskSoundEnabled, writeMiniTaskSoundEnabled,
} from './soundEffects';
import BoardErrorState from './components/BoardErrorState';
import RoundSummary from './components/RoundSummary';
import RoundView from './components/RoundView';
import useListeningAnswer, { restoredListeningProgress } from './listening/useListeningAnswer';
import MemoryRoundView from './memory/MemoryRoundView';
import ReflexRoundView from './reflex/ReflexRoundView';
import DigitRecallRoundView from './digit-recall/DigitRecallRoundView';
import OddOneOutRoundView from './odd-one-out/OddOneOutRoundView';
import CargoSortingRoundView from './cargo-sorting/CargoSortingRoundView';
import TurkishL1RoundView from './sound-hunt/TurkishL1RoundView';
import TurkishL1TourView from './sound-hunt/TurkishL1TourView';
import PatternRecognitionRoundView from './pattern-recognition/PatternRecognitionRoundView';
import WeeklyReadingRoundView from './weekly-reading/WeeklyReadingRoundView';
import PreparedWordBuildRoundView from './word-build/PreparedWordBuildRoundView';
import { playLocalWordBuildAttempt } from './word-build/wordBuildLocalRound';
import { PARENT_MINI_TASK_STUDENT_HINT_KEY } from './parentMiniTaskSession';
import { getStudentAvatar } from '../shared/studentAvatar';
import ParentStudentPageLayout from '../parent/ParentStudentPageLayout';
import { useParentNavigate } from '../parent/ParentNavigation';
import {
  clearActiveWordBuildRound,
  clearWordBuildLocalProgress,
  readActiveWordBuildRound,
  readWordBuildLocalProgress,
  writeActiveWordBuildRound,
  writeWordBuildLocalProgress,
} from './word-build/wordBuildSessionStorage';

const ANSWER_FEEDBACK_DELAY_MS = 2200;
const dayCompleteMessage = (roundLimit = 12) => `Bugünün ${roundLimit} turunu tamamladın! İyi ki birlikte öğrendik. Şimdi dinlenme zamanı; yarın yine buluşalım! 💛`;
const PARENT_SESSION_KEY_PREFIX = 'parent_mini_tasks_board_session:';

function parentSessionStorageKey(studentId) {
  return `${PARENT_SESSION_KEY_PREFIX}${studentId}`;
}

function readStoredParentSession(studentId) {
  try {
    const key = parentSessionStorageKey(studentId);
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.board_session_id || !parsed?.expires_at) return null;
    if (new Date(parsed.expires_at).getTime() <= Date.now()) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeStoredParentSession(studentId, boardSession) {
  try {
    window.sessionStorage.setItem(parentSessionStorageKey(studentId), JSON.stringify({
      board_session_id: boardSession?.id,
      expires_at: boardSession?.expires_at,
      completed: false,
    }));
  } catch (_) {
    // sessionStorage can be unavailable; backend remains authoritative.
  }
}

function markStoredParentSessionCompleted(studentId, boardSession) {
  try {
    window.sessionStorage.setItem(parentSessionStorageKey(studentId), JSON.stringify({
      board_session_id: boardSession?.id,
      expires_at: boardSession?.expires_at,
      completed: true,
    }));
  } catch (_) {
    // sessionStorage can be unavailable; in-memory state still handles this view.
  }
}

function isSessionLimitError(err) {
  return String(err?.message || '').toLocaleLowerCase('tr-TR').includes('çalışma tamamlandı');
}

function studentLabel(student) {
  const joined = [student?.first_name, student?.last_name].filter(Boolean).join(' ').trim();
  return joined || student?.display_name || student?.student_id || student?.id || 'Öğrenci';
}

function ParentSessionComplete({ canChooseAnother, error, onChooseAnother, onPlayAgain, restarting, language = 'tr', dayCompleted = false, roundLimit = 12, completedRounds = 0, completedInSession = 3 }) {
  const parentNavigate = useParentNavigate();
  const isEnglish = language === 'en';
  const replayCount = Math.min(3, Math.max(0, roundLimit - completedRounds));
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fffaf0', color: '#172554', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 4 } }}>
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => parentNavigate('/student360')}
          sx={{ mb: 2, color: '#1d4ed8', fontWeight: 800 }}
        >
          {isEnglish ? 'Student360' : 'Öğrenci360'}
        </Button>
        <Box
          sx={{
            bgcolor: '#ffffff',
            border: '1px solid #dbeafe',
            borderRadius: 3,
            p: { xs: 3, sm: 4 },
            boxShadow: '0 16px 42px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <CheckCircleRoundedIcon sx={{ color: '#16a34a', fontSize: 34 }} />
            <Typography variant="h4" sx={{ fontWeight: 1000, letterSpacing: 0, fontSize: { xs: 26, sm: 34 } }}>
              {dayCompleted ? 'Bugünün mini görevleri tamamlandı! 💛' : isEnglish ? `You completed ${completedInSession} Mini Tasks!` : `${completedInSession} mini görevi tamamladın!`}
            </Typography>
          </Box>
          <Typography sx={{ color: '#475569', fontSize: 17, lineHeight: 1.6, mb: 3 }}>
            {dayCompleted ? dayCompleteMessage(roundLimit) : isEnglish
              ? `Great work! You can return to Student360 or play ${replayCount} more rounds.`
              : `Harika çalıştın! İstersen Öğrenci360'a dönebilir veya ${replayCount} tur daha oynayabilirsin.`}
          </Typography>
          {error ? (
            <Box sx={{ mb: 2, p: 2, border: '1px solid #fecaca', bgcolor: '#fff1f2', borderRadius: 2, color: '#991b1b', fontWeight: 700 }}>
              {error}
            </Box>
          ) : null}
          <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={restarting ? <CircularProgress size={20} color="inherit" /> : <ReplayRoundedIcon />}
              disabled={restarting || dayCompleted}
              onClick={onPlayAgain}
              sx={{ borderRadius: 2, fontWeight: 900, textTransform: 'none' }}
            >
              {isEnglish ? `Play ${replayCount} more rounds` : `${replayCount} tur daha oyna`}
            </Button>
            <Button
              variant="outlined"
              size="large"
              disabled={restarting}
              onClick={() => parentNavigate('/student360')}
              sx={{ borderRadius: 2, fontWeight: 900, textTransform: 'none' }}
            >
              {isEnglish ? 'Back to Student360' : "Öğrenci360'a dön"}
            </Button>
            {canChooseAnother ? (
              <Button
                variant="outlined"
                size="large"
                disabled={restarting}
                onClick={onChooseAnother}
                sx={{ borderRadius: 2, fontWeight: 900, textTransform: 'none' }}
              >
                {isEnglish ? 'Choose another student' : 'Diğer öğrenciyi seç'}
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default function ParentMiniTaskPage() {
  const [students, setStudents] = useState([]);
  const [headerStudentId, setHeaderStudentId] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [boardSession, setBoardSession] = useState(null);
  const [activeRound, setActiveRound] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [roundSummary, setRoundSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [launchingId, setLaunchingId] = useState('');
  const [error, setError] = useState('');
  const [submittingChoice, setSubmittingChoice] = useState(null);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [roundAnswers, setRoundAnswers] = useState([]);
  const roundAnswersRef = useRef([]);
  const wordBuildAttemptsRef = useRef([]);
  const timeoutInFlightRef = useRef(false);
  const [timeoutState, setTimeoutState] = useState({ expired: false, saving: false, error: '' });
  const [roundCorrectCount, setRoundCorrectCount] = useState(0);
  const [completedStudents, setCompletedStudents] = useState({});
  const completedStudentsRef = useRef({});
  const [soundEnabled, setSoundEnabled] = useState(() => readMiniTaskSoundEnabled());

  const boardSessionId = boardSession?.id;
  const listeningAnswer = useListeningAnswer({ round: activeRound, question: currentQuestion, boardSessionId });
  const classTitle = useMemo(() => {
    const name = selectedStudent ? studentLabel(selectedStudent) : '';
    return name ? `${name} - Mini Görevler` : 'Mini Görevler';
  }, [selectedStudent]);

  const markStudentCompleted = useCallback((student, session) => {
    const sid = String(student?.student_id || student?.id || '');
    if (!sid) return;
    const next = {
      ...completedStudentsRef.current,
      [sid]: {
        expires_at: session?.expires_at,
      },
    };
    completedStudentsRef.current = next;
    setCompletedStudents(next);
    markStoredParentSessionCompleted(sid, session);
  }, []);

  const addCompletedStudent = useCallback((sid, expiresAt) => {
    const next = {
      ...completedStudentsRef.current,
      [sid]: { expires_at: expiresAt },
    };
    completedStudentsRef.current = next;
    setCompletedStudents(next);
  }, []);

  const replaceCompletedStudents = useCallback((next) => {
    completedStudentsRef.current = next;
    setCompletedStudents(next);
  }, []);

  const launchForStudent = useCallback(async (
    student,
    explicitSessionId,
    forceNewSession = false,
    options = {},
  ) => {
    const sid = String(student?.student_id || student?.id || '');
    if (!sid) return;
    prepareMiniTaskSound(readMiniTaskSoundEnabled());
    const hasRecommendedReading = student?.recommended_task?.task_type === 'weekly_reading' || !!options.readingAssignmentId;
    if (completedStudentsRef.current[sid] && !forceNewSession && !hasRecommendedReading) {
      setSelectedStudent(student);
      setActiveRound(null);
      setCurrentQuestion(null);
      setRoundSummary({
        task_type: 'parent_session_complete',
        rounds_remaining: 0,
      });
      return true;
    }
    setLaunchingId(sid);
    setError('');
    try {
      const stored = forceNewSession ? null : readStoredParentSession(sid);
      if (stored?.completed && !hasRecommendedReading) {
        setSelectedStudent(student);
        setBoardSession({
          id: stored.board_session_id,
          expires_at: stored.expires_at,
        });
        addCompletedStudent(sid, stored.expires_at);
        setRoundSummary({
          task_type: 'parent_session_complete',
          rounds_remaining: 0,
        });
        return true;
      }
      const launchArgs = [sid, explicitSessionId || stored?.board_session_id];
      if (options.skipWeeklyReading || options.readingAssignmentId) launchArgs.push(options);
      const payload = await launchParentMiniTask(...launchArgs);
      setSelectedStudent(student);
      setBoardSession(payload.board_session);
      writeStoredParentSession(sid, payload.board_session);
      const previousWordBuildRoundId = readActiveWordBuildRound(payload.board_session.id);
      if (previousWordBuildRoundId && previousWordBuildRoundId !== String(payload.round?.round_id || '')) {
        clearWordBuildLocalProgress(previousWordBuildRoundId);
        clearActiveWordBuildRound(payload.board_session.id);
      }
      wordBuildAttemptsRef.current = [];
      let launchedRound = payload.round;
      if (payload.round?.task_type === 'word_build') {
        if (payload.round.mode !== 'local_batch') {
          throw new Error('Kelimeyi Kur turu toplu kayda uygun değil.');
        }
        const local = readWordBuildLocalProgress(payload.round.round_id);
        if (local?.round?.round_id === payload.round.round_id && Array.isArray(local.attempts)) {
          launchedRound = local.round;
          wordBuildAttemptsRef.current = local.attempts;
        } else {
          writeWordBuildLocalProgress(payload.round, []);
        }
        writeActiveWordBuildRound(payload.board_session.id, payload.round.round_id);
      }
      setActiveRound(launchedRound);
      const restored = restoredListeningProgress(launchedRound);
      setCurrentQuestion(restored?.question || payload.round?.questions?.[0] || null);
      setFeedback(null);
      setRoundSummary(payload.round?.summary || null);
      setSelectedChoice(null);
      setCorrectAnswer(null);
      setRoundAnswers(restored?.answers || []);
      roundAnswersRef.current = restored?.answers || [];
      setTimeoutState({ expired: false, saving: false, error: '' });
      setRoundCorrectCount(restored?.correctCount || 0);
      return true;
    } catch (err) {
      if (/Bugünün \d+ turunu/.test(String(err?.message || ''))) {
        const bootstrap = await fetchParentMiniTaskBootstrap();
        setStudents(bootstrap?.students || []);
        setActiveRound(null);
        setRoundSummary(null);
        return false;
      }
      if (isSessionLimitError(err)) {
        const stored = readStoredParentSession(sid);
        const session = stored ? { id: stored.board_session_id, expires_at: stored.expires_at } : null;
        setSelectedStudent(student);
        setBoardSession(session);
        markStudentCompleted(student, session);
        setRoundSummary({
          task_type: 'parent_session_complete',
          rounds_remaining: 0,
        });
        return true;
      } else {
        setError(err?.message || 'Mini görev başlatılamadı.');
        return false;
      }
    } finally {
      setLaunchingId('');
      setLoading(false);
    }
  }, [addCompletedStudent, markStudentCompleted]);

  const loadEntry = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const bootstrap = await fetchParentMiniTaskBootstrap();
      const items = bootstrap?.students || [];
      setStudents(items);
      replaceCompletedStudents(items.reduce((acc, item) => {
        const sid = String(item.student_id || item.id || '');
        const stored = sid ? readStoredParentSession(sid) : null;
        if (stored?.completed) {
          acc[sid] = { expires_at: stored.expires_at };
        }
        return acc;
      }, {}));
      const entryQuery = new URLSearchParams(window.location.search);
      const readingAssignmentId = entryQuery.get('weekly_reading_id');
      const readingStudent = items.find((item) => String(item.student_id) === entryQuery.get('student_id'));
      if (readingAssignmentId) {
        if (!readingStudent || !/^\d+$/.test(readingAssignmentId)) throw new Error('Okuma ataması için öğrenci bulunamadı.');
        await launchForStudent(readingStudent, undefined, true, { readingAssignmentId });
        return;
      }
      const hintedId = (() => {
        try { return window.sessionStorage.getItem(PARENT_MINI_TASK_STUDENT_HINT_KEY) || ''; } catch (_) { return ''; }
      })();
      const hinted = items.find((item) => String(item.student_id) === String(hintedId));
      const autoStudent = hinted || (items.length === 1 ? items[0] : null);
      if (autoStudent && !autoStudent.daily_progress?.day_completed) {
        await launchForStudent(autoStudent);
        return;
      }
    } catch (err) {
      setError(err?.message || 'Mini görev bilgileri alınamadı.');
    } finally {
      setLoading(false);
    }
  }, [launchForStudent, replaceCompletedStudents]);

  useEffect(() => {
    loadEntry();
  }, [loadEntry]);

  useEffect(() => {
    if (!roundSummary) return;
    let current = true;
    fetchParentMiniTaskBootstrap().then((bootstrap) => {
      if (current) setStudents(bootstrap?.students || []);
    }).catch(() => {});
    return () => { current = false; };
  }, [roundSummary]);

  const handleToggleSound = () => {
    setSoundEnabled((current) => {
      const next = !current;
      writeMiniTaskSoundEnabled(next);
      if (next) playMiniTaskSound('correct', true);
      return next;
    });
  };

  const recoverCurrentActivity = () => {
    clearMiniTaskCountdown(activeRound.round_id);
    setActiveRound(null);
    setCurrentQuestion(null);
    setFeedback(null);
    setSubmittingChoice(null);
    return launchForStudent(selectedStudent, boardSessionId);
  };

  const handleAnswer = async (choice) => {
    if (!activeRound?.round_id || !currentQuestion?.id || !boardSessionId) return;
    const listeningResult = activeRound.listening_contract ? await listeningAnswer.grade(choice) : null;
    if (activeRound.listening_contract && !listeningResult) return;
    setSubmittingChoice(choice);
    setSelectedChoice(choice);
    setCorrectAnswer(null);
    setFeedback(null);
    const isCorrect = listeningResult ? listeningResult.is_correct : String(choice) === String(currentQuestion.correct_answer);
    const answerText = listeningResult ? listeningResult.correct_answer : currentQuestion.correct_answer;
    const nextCorrectCount = roundCorrectCount + (isCorrect ? 1 : 0);
    const questions = activeRound.questions || [];
    const currentIndex = questions.findIndex((question) => question.id === currentQuestion.id);
    const nextQuestion = questions[currentIndex + 1] || null;
    const nextAnswers = [
      ...roundAnswers,
      { question_id: currentQuestion.id, selected_answer: choice },
    ];
    setCorrectAnswer(answerText);
    const isEnglishRound = activeRound?.lesson?.code === 'ENGLISH';
    setFeedback({
      type: isCorrect ? 'correct' : 'near',
      message: isEnglishRound
        ? (isCorrect ? 'Great job!' : `Almost! The correct answer is ${answerText}.`)
        : (isCorrect ? 'Harika, doğru!' : `Neredeyse! Doğru cevap ${answerText}.`),
    });
    setRoundAnswers(nextAnswers);
    roundAnswersRef.current = nextAnswers;
    setRoundCorrectCount(nextCorrectCount);
    playMiniTaskSound(isCorrect ? 'correct' : 'wrong', soundEnabled);

    if (nextQuestion) {
      window.setTimeout(() => {
        setCurrentQuestion(nextQuestion);
        setFeedback(null);
        setSelectedChoice(null);
        setCorrectAnswer(null);
        setSubmittingChoice(null);
      }, ANSWER_FEEDBACK_DELAY_MS);
      return;
    }

    if (activeRound.listening_contract) return; // Keep completion/reward gates closed.
    try {
      const summary = (await completeRound(activeRound.round_id, boardSessionId, nextAnswers)).summary;
      clearMiniTaskCountdown(activeRound.round_id);
      if (Number(summary?.rounds_remaining || 0) <= 0) {
        markStudentCompleted(selectedStudent, boardSession);
      }
      window.setTimeout(() => {
        playMiniTaskSound('complete', soundEnabled);
        setCurrentQuestion(null);
        setRoundSummary(summary);
        setSubmittingChoice(null);
      }, ANSWER_FEEDBACK_DELAY_MS);
    } catch (err) {
      if (isProgressChangedError(err)) {
        await recoverCurrentActivity();
        return;
      }
      setFeedback({ type: 'near', message: err?.message || (isEnglishRound ? 'The result could not be saved.' : 'Tur sonucu kaydedilemedi.') });
      setRoundAnswers(roundAnswers);
      roundAnswersRef.current = roundAnswers;
      setRoundCorrectCount(roundCorrectCount);
      setSubmittingChoice(null);
      window.setTimeout(() => {
        setFeedback(null);
        setSelectedChoice(null);
        setCorrectAnswer(null);
      }, 1800);
    }
  };

  const handleRoundTimeout = async () => {
    if (activeRound?.listening_contract || currentQuestion?.listening) return;
    if (!activeRound?.round_id || !boardSessionId || timeoutInFlightRef.current) return;
    timeoutInFlightRef.current = true;
    setTimeoutState({ expired: true, saving: true, error: '' });
    try {
      const payload = await completeRound(activeRound.round_id, boardSessionId, roundAnswersRef.current, 'timer');
      clearMiniTaskCountdown(activeRound.round_id);
      if (Number(payload?.summary?.rounds_remaining || 0) <= 0) {
        markStudentCompleted(selectedStudent, boardSession);
      }
      setCurrentQuestion(null);
      setRoundSummary(payload.summary);
    } catch (err) {
      if (isProgressChangedError(err)) {
        await recoverCurrentActivity();
        return;
      }
      setTimeoutState({ expired: true, saving: false, error: err?.message || 'Sonuç kaydedilemedi.' });
    } finally {
      timeoutInFlightRef.current = false;
    }
  };

  const handleBackToPicker = () => {
    setActiveRound(null);
    setCurrentQuestion(null);
    setRoundSummary(null);
    setFeedback(null);
  };

  const handleSkipWeeklyReading = async () => {
    if (!selectedStudent) return false;
    return launchForStudent(selectedStudent, boardSessionId, false, { skipWeeklyReading: true });
  };

  const handleWeeklyReadingCompleted = (result) => {
    if (result?.attempt_type !== 'official' || result?.measurement_status !== 'valid') return;
    const selectedId = String(selectedStudent?.student_id || selectedStudent?.id || '');
    setStudents((current) => current.map((student) => (
      String(student?.student_id || student?.id || '') === selectedId
        ? { ...student, recommended_task: null }
        : student
    )));
  };

  const handleContinue = async () => {
    if (selectedStudent) return launchForStudent(selectedStudent, boardSessionId);
    return false;
  };

  const handleChooseAnother = () => {
    setActiveRound(null);
    setCurrentQuestion(null);
    setRoundSummary(null);
    setFeedback(null);
    setSelectedStudent(null);
  };

  const handlePlayAgain = async () => {
    if (!selectedStudent) return false;
    const sid = String(selectedStudent.student_id || selectedStudent.id || '');
    const started = await launchForStudent(selectedStudent, undefined, true);
    if (started && sid) {
      const next = { ...completedStudentsRef.current };
      delete next[sid];
      replaceCompletedStudents(next);
    }
    return started;
  };

  const handleMemoryExit = async () => {
    handleBackToPicker();
  };

  const handleWordBuildExit = () => {
    if (boardSessionId) clearActiveWordBuildRound(boardSessionId);
    if (activeRound?.round_id) clearWordBuildLocalProgress(activeRound.round_id);
    wordBuildAttemptsRef.current = [];
    handleBackToPicker();
  };

  const handleWordBuildAttempt = ({ questionId, answer, idempotencyKey }) => {
    if (!activeRound?.round_id || !boardSessionId) {
      throw new Error('Tur bilgisi bulunamadı.');
    }
    const { attempts, result } = playLocalWordBuildAttempt(
      activeRound, boardSessionId, wordBuildAttemptsRef.current,
      { questionId, answer, idempotencyKey },
    );
    wordBuildAttemptsRef.current = attempts;
    return result;
  };

  const handleWordBuildComplete = (summary) => {
    if (boardSessionId) clearActiveWordBuildRound(boardSessionId);
    if (activeRound?.round_id) clearWordBuildLocalProgress(activeRound.round_id);
    wordBuildAttemptsRef.current = [];
    if (Number(summary?.rounds_remaining || 0) <= 0) {
      markStudentCompleted(selectedStudent, boardSession);
    }
    setRoundSummary(summary);
  };

  const handleWordBuildTimeout = async () => {
    if (!activeRound?.round_id || !boardSessionId) throw new Error('Tur bilgisi bulunamadı.');
    const attempts = wordBuildAttemptsRef.current.map(({ question_id, answer }) => ({ question_id, answer }));
    const payload = await timeoutWordBuildRound(activeRound.round_id, boardSessionId, attempts);
    return payload.summary;
  };

  const handleMemoryComplete = async (attemptCounts, finishReason = 'completed', matchedCounts = null) => {
    if (!activeRound?.round_id || !boardSessionId) return;
    const payload = await completeMemoryRound(activeRound.round_id, boardSessionId, attemptCounts, finishReason, matchedCounts);
    if (Number(payload?.summary?.rounds_remaining || 0) <= 0) {
      markStudentCompleted(selectedStudent, boardSession);
    }
    setRoundSummary(payload.summary);
  };

  const handleDigitRecallExit = async () => {
    handleBackToPicker();
  };

  const handleDigitRecallComplete = async (responses, finishReason = 'completed') => {
    if (!activeRound?.round_id || !boardSessionId) return;
    const payload = await completeDigitRecallRound(activeRound.round_id, boardSessionId, responses, finishReason);
    if (Number(payload?.summary?.rounds_remaining || 0) <= 0) {
      markStudentCompleted(selectedStudent, boardSession);
    }
    setRoundSummary(payload.summary);
  };

  const handleReflexComplete = async (score, interactionCount) => {
    if (!activeRound?.round_id || !boardSessionId) return;
    const payload = await completeReflexRound(activeRound.round_id, boardSessionId, score, interactionCount);
    if (Number(payload?.summary?.rounds_remaining || 0) <= 0) {
      markStudentCompleted(selectedStudent, boardSession);
    }
    setRoundSummary(payload.summary);
  };

  const handleOddOneOutExit = () => {
    handleBackToPicker();
  };

  const handleOddOneOutComplete = async (events, finishReason) => {
    if (!activeRound?.round_id || !boardSessionId) return;
    const payload = await completeOddOneOutRound(activeRound.round_id, boardSessionId, events, finishReason);
    if (Number(payload?.summary?.rounds_remaining || 0) <= 0) {
      markStudentCompleted(selectedStudent, boardSession);
    }
    setRoundSummary(payload.summary);
  };

  const handleCargoSortingComplete = async (counts) => {
    if (!activeRound?.round_id || !boardSessionId) return;
    const payload = await completeCargoSortingRound(activeRound.round_id, boardSessionId, counts);
    if (Number(payload?.summary?.rounds_remaining || 0) <= 0) {
      markStudentCompleted(selectedStudent, boardSession);
    }
    setRoundSummary(payload.summary);
  };

  const handlePatternRecognitionComplete = async (rounds) => {
    if (!activeRound?.round_id || !boardSessionId) return;
    const payload = await completePatternRecognitionRound(activeRound.round_id, boardSessionId, rounds);
    if (Number(payload?.summary?.rounds_remaining || 0) <= 0) {
      markStudentCompleted(selectedStudent, boardSession);
    }
    setRoundSummary(payload.summary);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fffaf0' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !students.length && !activeRound) {
    return <BoardErrorState message={error} onRetry={loadEntry} />;
  }

  if (roundSummary) {
    if (Number(roundSummary?.rounds_remaining || 0) <= 0) {
      return (
        <ParentSessionComplete
          dayCompleted={Boolean(students.find((student) => String(student.student_id) === String(selectedStudent?.student_id))?.daily_progress?.day_completed)}
          roundLimit={students.find((student) => String(student.student_id) === String(selectedStudent?.student_id))?.daily_progress?.round_limit || 12}
          completedRounds={students.find((student) => String(student.student_id) === String(selectedStudent?.student_id))?.daily_progress?.completed_rounds || 0}
          completedInSession={roundSummary?.rounds_used || 3}
          canChooseAnother={students.length > 1}
          error={error}
          onChooseAnother={handleChooseAnother}
          onPlayAgain={handlePlayAgain}
          restarting={Boolean(launchingId)}
          language={roundSummary?.locale === 'en' ? 'en' : 'tr'}
        />
      );
    }
    return <RoundSummary summary={roundSummary} onBackToBoard={handleBackToPicker} onContinue={handleContinue} />;
  }

  if (activeRound?.task_type === 'word_build') {
    return (
      <PreparedWordBuildRoundView
        key={`${boardSessionId}:${activeRound.round_id}`}
        round={activeRound}
        boardSessionId={boardSessionId}
        classTitle={classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onExit={handleWordBuildExit}
        onAttempt={handleWordBuildAttempt}
        onRoundChange={setActiveRound}
        onComplete={handleWordBuildComplete}
        onTimeout={handleWordBuildTimeout}
      />
    );
  }

  if (activeRound?.task_type === 'weekly_reading') {
    return (
      <WeeklyReadingRoundView
        round={activeRound}
        boardSessionId={boardSessionId}
        classTitle={classTitle}
        onExit={handleBackToPicker}
        onSkip={handleSkipWeeklyReading}
        onCompleted={handleWeeklyReadingCompleted}
      />
    );
  }

  if (activeRound?.task_type === 'memory_matching') {
    return (
      <MemoryRoundView
        round={activeRound}
        boardSessionId={boardSessionId}
        classTitle={classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onExit={handleMemoryExit}
        onComplete={handleMemoryComplete}
      />
    );
  }

  if (activeRound?.task_type === 'reflex_catch') {
    return (
      <ReflexRoundView
        round={activeRound}
        classTitle={classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onExit={handleBackToPicker}
        onComplete={handleReflexComplete}
      />
    );
  }

  if (activeRound?.task_type === 'digit_recall') {
    return (
      <DigitRecallRoundView
        round={activeRound}
        boardSessionId={boardSessionId}
        classTitle={classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onExit={handleDigitRecallExit}
        onComplete={handleDigitRecallComplete}
      />
    );
  }

  if (activeRound?.task_type === 'odd_one_out') {
    return (
      <OddOneOutRoundView
        round={activeRound}
        classTitle={classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onExit={handleOddOneOutExit}
        onComplete={handleOddOneOutComplete}
      />
    );
  }

  if (activeRound?.task_type === 'turkish_l1_tour') {
    return <TurkishL1TourView key={activeRound.round_id} round={activeRound} boardSessionId={boardSessionId}
      classTitle={classTitle} onComplete={(summary) => {
        if (Number(summary?.rounds_remaining || 0) <= 0) markStudentCompleted(selectedStudent, boardSession);
        setRoundSummary(summary);
      }} onExit={handleBackToPicker} />;
  }
  if (['sound_hunt', 'letter_hunt', 'sound_discovery', 'target_sound_hunt'].includes(activeRound?.task_type)) {
    return <TurkishL1RoundView key={activeRound.round_id} round={activeRound} boardSessionId={boardSessionId}
      classTitle={classTitle} soundEnabled={soundEnabled} onToggleSound={handleToggleSound}
      onComplete={(summary) => {
        if (Number(summary?.rounds_remaining || 0) <= 0) markStudentCompleted(selectedStudent, boardSession);
        setRoundSummary(summary);
      }} onExit={handleBackToPicker} />;
  }

  if (activeRound?.task_type === 'cargo_sorting') {
    return (
      <CargoSortingRoundView
        round={activeRound}
        classTitle={classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onExit={handleBackToPicker}
        onComplete={handleCargoSortingComplete}
      />
    );
  }

  if (activeRound?.task_type === 'pattern_recognition') {
    return (
      <PatternRecognitionRoundView
        round={activeRound}
        boardSessionId={boardSessionId}
        classTitle={classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onExit={handleBackToPicker}
        onComplete={handlePatternRecognitionComplete}
      />
    );
  }

  if (activeRound && currentQuestion) {
    return (
      <RoundView
        round={activeRound}
        answerPending={listeningAnswer.grading}
        answerError={listeningAnswer.error}
        boardSessionId={boardSessionId}
        onTimeout={handleRoundTimeout}
        timedOut={timeoutState.expired}
        timeoutSaving={timeoutState.saving}
        timeoutError={timeoutState.error}
        timerActive={!Boolean(submittingChoice)}
        question={currentQuestion}
        feedback={feedback}
        submittingChoice={submittingChoice}
        selectedChoice={selectedChoice}
        correctAnswer={correctAnswer}
        classTitle={classTitle}
        soundEnabled={soundEnabled}
        onToggleSound={handleToggleSound}
        onBackToBoard={handleBackToPicker}
        onAnswer={handleAnswer}
      />
    );
  }

  const headerStudent = students.find((student) => String(student.student_id) === headerStudentId)
    || selectedStudent || students[0];
  return (
    <ParentStudentPageLayout studentId={headerStudent?.student_id} studentFallback={headerStudent} students={students} onStudentChange={setHeaderStudentId} fetchPointBalance activePage="miniTasks">
      <Box>
        <Typography sx={{ color: '#475569', mb: 3 }}>
          Çalışmak isteyen öğrenciyi seçin.
        </Typography>
        {error ? (
          <Box sx={{ mb: 2, p: 2, border: '1px solid #fecaca', bgcolor: '#fff1f2', borderRadius: 2, color: '#991b1b', fontWeight: 700 }}>
            {error}
          </Box>
        ) : null}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
          {students.map((student) => {
            const sid = String(student.student_id);
            const avatarTheme = getStudentAvatar(sid, student.avatar_id, student.starter_emoji);
            const busy = launchingId === sid;
            const hasReading = student?.recommended_task?.task_type === 'weekly_reading';
            const dayCompleted = Boolean(student.daily_progress?.day_completed);
            const completed = dayCompleted || (Boolean(completedStudents[sid]) && !hasReading);
            return (
              <Box key={sid} sx={{ display: 'grid', gap: 0.5 }}>
              <Button
                variant={completed ? 'outlined' : 'contained'}
                endIcon={busy ? <CircularProgress size={18} color="inherit" /> : completed ? <ReplayRoundedIcon /> : <PlayArrowRoundedIcon />}
                disabled={Boolean(launchingId) || dayCompleted}
                onClick={() => launchForStudent(student)}
                sx={{
                  minHeight: completed ? 88 : 74,
                  justifyContent: 'space-between',
                  textAlign: 'left',
                  bgcolor: completed ? '#f8fafc' : '#2563eb',
                  borderColor: completed ? '#cbd5e1' : undefined,
                  color: completed ? '#475569' : undefined,
                  borderRadius: 2,
                  px: 2,
                  fontWeight: 900,
                  textTransform: 'none',
                  '&.Mui-disabled': {
                    opacity: 0.65,
                  },
                }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {avatarTheme.imageUrl ? <Box component="img" src={avatarTheme.imageUrl} alt="" loading="lazy" decoding="async"
                      sx={{ width: 44, height: 44, objectFit: 'contain' }} />
                      : <Box component="span" aria-hidden="true" sx={{ fontSize: 32 }}>{avatarTheme.avatar}</Box>}
                    <span>{studentLabel(student)}</span>
                  </Box>
                  {student.grade ? <Box sx={{ fontSize: 13, opacity: 0.85 }}>{student.grade}. sınıf</Box> : null}
                  {hasReading ? (
                    <Box sx={{ mt: 0.7, fontSize: 13, fontWeight: 900 }}>
                      Okuma hazır: {student.recommended_task.title}
                    </Box>
                  ) : null}
                  {completed ? (
                    <Box sx={{ mt: 0.7, fontSize: 13, fontWeight: 800, color: '#64748b' }}>
                      {dayCompleted ? dayCompleteMessage(student.daily_progress?.round_limit || 12) : '3 tur tamamlandı · Tekrar oynamak için dokunun'}
                    </Box>
                  ) : null}
                </Box>
              </Button>
              </Box>
            );
          })}
        </Box>
      </Box>
    </ParentStudentPageLayout>
  );
}
